import type { Report } from "../types/reports";
import type { ReportCardRangeStats } from "./reportListCardPreview";
import { extractReportRangeStats } from "./reportListCardPreview";
import { getEvidenceDocumentRoot } from "./reportEvidencePointer";
import { formatReportWindowLabelForDisplay, getReportRunTimezoneForDisplay } from "./reportWindowLabel";

export type ReportCohortPeriod = {
  kind?: string;
  display_label?: string;
  local_start_date?: string;
  local_end_date?: string;
};

function asRecord(o: unknown): Record<string, unknown> | null {
  return o && typeof o === "object" && !Array.isArray(o) ? (o as Record<string, unknown>) : null;
}

function pickNonEmptyString(...candidates: unknown[]): string | null {
  for (const c of candidates) {
    if (typeof c === "string") {
      const t = c.trim();
      if (t) return t;
    }
  }
  return null;
}

function toFiniteNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Prefer API list/detail cohort fields; fall back to snapshot meta/stats for older rows. */
export function extractReportCohortSummary(report: Report | null | undefined): ReportCardRangeStats | null {
  if (!report) return null;

  const topPnl = toFiniteNumber(report.cohort_total_pnl);
  const topWr = toFiniteNumber(report.cohort_win_rate);
  const topTrades =
    toFiniteNumber(report.cohort_total_trades) ?? toFiniteNumber(report.cohort_closed_trades);

  if (topPnl != null || topWr != null || topTrades != null) {
    return {
      totalPnl: Math.round((topPnl ?? 0) * 100) / 100,
      winRate: Math.round((topWr ?? 0) * 100) / 100,
      totalTrades: Math.max(0, Math.round(topTrades ?? 0)),
    };
  }

  const snapRec = asRecord(report.input_snapshot);
  const meta = asRecord(snapRec?.meta);
  const cohortSummary = asRecord(meta?.cohort_summary);
  if (cohortSummary) {
    const pnl = toFiniteNumber(cohortSummary.total_pnl);
    const wr = toFiniteNumber(cohortSummary.win_rate);
    const trades =
      toFiniteNumber(cohortSummary.total_trades) ?? toFiniteNumber(cohortSummary.closed_trades);
    if (pnl != null || wr != null || trades != null) {
      return {
        totalPnl: Math.round((pnl ?? 0) * 100) / 100,
        winRate: Math.round((wr ?? 0) * 100) / 100,
        totalTrades: Math.max(0, Math.round(trades ?? 0)),
      };
    }
  }

  return extractReportRangeStats(report.input_snapshot);
}

/** OHLC bar size for market context (not the trade cohort preset `timeframe`). */
export function getReportCandleTimeframeForDisplay(report: Report | null | undefined): string | null {
  if (!report) return null;
  const rep = report as unknown as Record<string, unknown>;
  const snapRec = asRecord(report.input_snapshot);
  const snapMeta = asRecord(snapRec?.meta);
  const snapRequest = asRecord(snapRec?.request);
  const bundleMeta = asRecord(getEvidenceDocumentRoot(report.input_snapshot));

  return pickNonEmptyString(
    report.candle_timeframe,
    rep.candle_timeframe,
    snapMeta?.candle_timeframe,
    snapRequest?.candle_timeframe,
    bundleMeta?.candle_timeframe
  );
}

/** Human-readable trade cohort window; prefers BE `cohort_period_*` over raw `timeframe`. */
export function getReportCohortWindowLabel(report: Report | null | undefined): string {
  if (!report) return "All time";

  const period = report.cohort_period as ReportCohortPeriod | undefined;
  const cohortLabel = pickNonEmptyString(report.cohort_period_label, period?.display_label);
  if (cohortLabel) return cohortLabel;

  const tz = getReportRunTimezoneForDisplay(report) ?? undefined;
  const fromDates = formatReportWindowLabelForDisplay(
    { start_date: report.start_date, end_date: report.end_date, timeframe: report.timeframe },
    { timeZone: tz, whenUnset: "all-time" }
  );
  if (fromDates && fromDates !== "All time") return fromDates;

  if (report.timeframe && report.timeframe.trim().toLowerCase() !== "all-time") {
    return formatReportWindowLabelForDisplay({ timeframe: report.timeframe }, { whenUnset: "all-time" }) ?? "All time";
  }

  return fromDates ?? "All time";
}
